import {
  Button,
  Center,
  Drawer,
  Flex,
  Grid,
  Loader,
  Pagination,
  Paper,
  Select,
  Stack,
  Text,
  Textarea,
  Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useCallback, useEffect, useState } from "react";
import {
  PredefinedMessage,
  PredefinedMessageCreateDTO,
  PredefinedMessageUpdateDTO,
} from "../models/PredefinedMessage";
import { useForm } from "@mantine/form";
import { fetchWithAuthentication } from "../utilities/FetchUtil";
import { convertToLocalDateTime } from "../utilities/DateUtil";

const ManagePredefinedMessages = () => {
  const [pageNum, setPageNum] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [createText, setCreateText] = useState("");
  const [updateText, setUpdateText] = useState("");
  const [createTextColor, setCreateTextColor] = useState("green");
  const [updateTextColor, setUpdateTextColor] = useState("green");
  const [createIsLoading, setCreateIsLoading] = useState(false);
  const [updateIsLoading, setUpdateIsLoading] = useState(false);
  const [listIsLoading, setListIsLoading] = useState(false);
  const [typesIsLoading, setTypesIsLoading] = useState(false);
  const [predefinedMessageList, setPredefinedMessageList] = useState<
    PredefinedMessage[]
  >([]);
  const [predefinedMessageTypes, setPredefinedMessageTypes] = useState<
    string[]
  >([]);
  const [selectedPredefinedMessage, setSelectedPredefinedMessage] =
    useState<PredefinedMessage>({
      id: "",
      type: "",
      content: "",
      createdAt: "",
    });
  const [predefinedMessageClicked, setPredefinedMessageClicked] = useState<
    number | null
  >(null);
  const [selectedType, setSelectedType] = useState<string>('');

  const [updateDrawerOpened, updateDrawerHandlers] = useDisclosure(false);
  const [createDrawerOpened, createDrawerHandlers] = useDisclosure(false);

  const createForm = useForm({
    mode: "uncontrolled",
    initialValues: {
      type: selectedType,
      content: "",
    },
    validate: {
      content: (value: string) => {
        if (value.length > 2000) {
          return "Content must be 2,000 characters or fewer.";
        }
        if (value.length < 4) {
          return "Content must be at least 4 characters.";
        }
        return null;
      },
    },
  });

  const updateForm = useForm({
    mode: "uncontrolled",
    initialValues: {
      id: "",
      content: "",
    },
    validate: {
      id: (value: string) => {
        if (value == null) {
          return "Predefined message must have an ID to be updated.";
        } else {
          return null;
        }
      },
      content: (value: string) => {
        if (value.length > 2000) {
          return "Content must be 2,000 characters or fewer.";
        }
        if (value.length < 4) {
          return "Content must be at least 4 characters.";
        }
        return null;
      },
    },
  });

  const getPredefinedMessages = useCallback(async () => {
    setListIsLoading(true);
    const response = await fetchWithAuthentication(
      `/chat/predefined?type=${selectedType}&pageNum=${pageNum}`,
      "GET",
      null,
    );
    if (response.ok) {
      const json = await response.json();
      setPredefinedMessageList(json.predefinedMessages);
      setTotalPages(json.pageTotal);
      setListIsLoading(false);
    } else {
      console.log("Something went wrong fetching predefined messages");
      setPredefinedMessageList([]);
      setListIsLoading(false);
    }
  }, [selectedType, pageNum]);

  const getPredefinedMessageTypes = useCallback(async () => {
    setTypesIsLoading(true);
    const response = await fetchWithAuthentication(
      '/chat/predefined/types',
      "GET",
      null,
    );
    if (response.ok) {
      const json = await response.json();
      setPredefinedMessageTypes(json.types);
      setTypesIsLoading(false);
    } else {
      console.log("Something went wrong fetching predefined message types");
      setPredefinedMessageTypes([]);
      setTypesIsLoading(false);
    }
  }, []);

  const createPredefinedMessage = async () => {
    setCreateIsLoading(true);
    const request: PredefinedMessageCreateDTO = {
      type: selectedType,
      content: createForm.getValues().content,
    };
    const response = await fetchWithAuthentication(
      "/chat/predefined",
      "POST",
      request,
    );
    if (response.ok) {
      setCreateTextColor("green");
      setCreateText("New predefined message successfully created.");
      setCreateIsLoading(false);
      getPredefinedMessages();
    } else if (response.status === 409) {
      setCreateTextColor("red");
      setCreateText(`There was an issue creating a predefined message.`);
      setCreateIsLoading(false);
    } else {
      setCreateTextColor("red");
      setCreateText(
        "Something went wrong while attempting to create new predefined messaeg",
      );
      setCreateIsLoading(false);
    }
    createForm.reset();
  };

  const updatePredefinedMessage = async () => {
    setUpdateIsLoading(true);
    const request: PredefinedMessageUpdateDTO = {
      predefinedMessageId: selectedPredefinedMessage.id,
      content: updateForm.getValues().content,
    };
    const response = await fetchWithAuthentication(
      "/chat/predefined",
      "PUT",
      request,
    );
    if (response.ok) {
      setUpdateTextColor("green");
      setUpdateText("Predefined message successfully updated.");
      setUpdateIsLoading(false);
      getPredefinedMessages();
    } else {
      setUpdateTextColor("red");
      setUpdateText(
        "An issue occurred while trying to update predefined message.",
      );
      setUpdateIsLoading(false);
    }
  };

  useEffect(() => {
    getPredefinedMessages();
    getPredefinedMessageTypes();
  }, [selectedType, pageNum, getPredefinedMessages, getPredefinedMessageTypes]);

  return (
    <>
      <Drawer
        offset={8}
        radius="md"
        position="right"
        opened={updateDrawerOpened}
        onClose={() => {
          updateDrawerHandlers.close();
          setUpdateText("");
        }}
        title="Update predefined message"
      >
        <form onSubmit={updateForm.onSubmit(updatePredefinedMessage)}>
          <Stack w={400}>
            <Textarea
              label="Content"
              {...updateForm.getInputProps("content")}
              key={`update-${updateForm.key("content")}`}
              autosize
              minRows={3}
              maxRows={10}
              placeholder="The content of the predefined message"
            />
            <Button type="submit">Update</Button>
            <Center mih={50}>
              {updateIsLoading ? (
                <Loader />
              ) : (
                <Text c={updateTextColor}>{updateText}</Text>
              )}
            </Center>
          </Stack>
        </form>
      </Drawer>

      <Drawer
        offset={8}
        radius="md"
        position="left"
        opened={createDrawerOpened}
        onClose={() => {
          createDrawerHandlers.close();
          setCreateText("");
        }}
        title="Create predefined message"
      >
        <form onSubmit={createForm.onSubmit(createPredefinedMessage)}>
          <Stack w={400}>
            <Select
              label="Predefined message type"
              {...createForm.getInputProps("type")}
              key={createForm.key("type")}
              allowDeselect={false}
              placeholder="The purpose of the predefined message"
              value={selectedType}
              onChange={(value) =>
                setSelectedType(value ?? 'NUDGE')
              }
              data={predefinedMessageTypes}
              rightSection={typesIsLoading ? <Loader size="xs" /> : null}
              disabled={typesIsLoading}
            />
            <Textarea
              label="Content"
              {...createForm.getInputProps("content")}
              key={createForm.key("content")}
              autosize
              minRows={3}
              maxRows={10}
              placeholder="The content of the predefined message"
            />
            <Button type="submit">Create</Button>
            <Center mih={50}>
              {createIsLoading ? (
                <Loader />
              ) : (
                <Text c={createTextColor}>{createText}</Text>
              )}
            </Center>
          </Stack>
        </form>
      </Drawer>

      <Stack justify="center" w="100%">
        <Paper p="lg" shadow="lg" radius="md" w="100%">
          <Flex direction="column" gap="lg" align="center" w="100%">
            <Title order={3}>Manage predefined messages</Title>
            <Select
              data={predefinedMessageTypes}
              value={selectedType}
              label="Select predefined message type"
              allowDeselect={false}
              onChange={(value) => {
                setSelectedType(value ?? 'NUDGE');
              }}
              rightSection={typesIsLoading ? <Loader size="xs" /> : null}
              disabled={typesIsLoading}
              placeholder="Select a type"
            />
            <Button onClick={createDrawerHandlers.open}>
              Create new predefined message
            </Button>
          </Flex>
        </Paper>
        <Paper p="md" shadow="lg" radius="md">
          <Stack>
            <Center>
              <Pagination
                value={pageNum}
                onChange={(value) => setPageNum(value)}
                total={totalPages}
                withEdges
              />
            </Center>

            <Grid>
              <Grid.Col span={2} fw={700}>
                Type
              </Grid.Col>
              <Grid.Col span={6} fw={700}>
                Content
              </Grid.Col>
              <Grid.Col span={3} fw={700}>
                Created
              </Grid.Col>
              <Grid.Col span={1} fw={700}>
                Edit
              </Grid.Col>
            </Grid>
          </Stack>
        </Paper>
        <Stack>
          {listIsLoading && (
            <Center>
              <Loader />
            </Center>
          )}
          {predefinedMessageList.length === 0 && !listIsLoading && (
            <Center>
              <Text>No predefined messages of type {selectedType} found.</Text>
            </Center>
          )}
          {predefinedMessageList.map((predefinedMessage, index) => {
            return (
              <Paper
                key={`predefinedMessage${predefinedMessage.id}`}
                p="md"
                shadow="lg"
                radius="md"
                style={{ cursor: "pointer" }}
                onClick={() => {
                  if (index === predefinedMessageClicked) {
                    setPredefinedMessageClicked(null);
                  } else {
                    setPredefinedMessageClicked(index);
                  }
                }}
              >
                <Grid align="center">
                  <Grid.Col span={2}>
                    <Text>{predefinedMessage.type}</Text>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Text
                      lineClamp={predefinedMessageClicked === index ? 100 : 2}
                    >
                      {predefinedMessage.content}
                    </Text>
                  </Grid.Col>
                  <Grid.Col span={3}>
                    <Text
                      lineClamp={predefinedMessageClicked === index ? 100 : 2}
                    >
                      {convertToLocalDateTime(predefinedMessage.createdAt)}
                    </Text>
                  </Grid.Col>
                  <Grid.Col span={1}>
                    <Button
                      onClick={() => {
                        updateForm.reset();
                        updateForm.setValues(predefinedMessage);
                        setSelectedPredefinedMessage(predefinedMessage);
                        updateDrawerHandlers.open();
                      }}
                    >
                      Edit
                    </Button>
                  </Grid.Col>
                </Grid>
              </Paper>
            );
          })}
        </Stack>
      </Stack>
    </>
  );
};

export default ManagePredefinedMessages;
